/**
 * Admin-only Rewards dashboard poller (read-only).
 *
 * Mirrors `useReviewSummary`'s lightweight strategy — fetch on mount, refresh on
 * focus / visibility / hash navigation, a modest 12s poll while visible, plus an
 * imperative `refresh()`. Surfaces the backend-authoritative rewards dashboard
 * (open payout requests + vault/liabilities truth). Fails soft: on error the
 * last known value is kept. Every fetch re-enforces `require_admin` server-side.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { rewardsApi, type AdminRewardsDashboard } from "../../lib/rewards-api";

const POLL_MS = 12000;

export function useRewardsDashboard(
  enabled: boolean,
): { dashboard: AdminRewardsDashboard | null; refresh: () => void } {
  const [dashboard, setDashboard] = useState<AdminRewardsDashboard | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const load = useCallback((signal?: AbortSignal) => {
    if (!enabledRef.current) return;
    rewardsApi
      .adminDashboard(signal)
      .then((d) => setDashboard(d))
      .catch(() => {});
  }, []);

  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    load(controller.signal);

    const bg = () => load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", bg);
    window.addEventListener("hashchange", bg);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") load();
    }, POLL_MS);

    return () => {
      controller.abort();
      window.removeEventListener("focus", bg);
      window.removeEventListener("hashchange", bg);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, load]);

  return { dashboard, refresh };
}
